import { Map } from 'immutable';
import mapKeys from 'lodash.mapkeys';
import { records as appStorageRecords } from '@saucelabs/sl-module-app-storage';

import { records as realDeviceSelectionRecords } from '../ducks/realDeviceSelection';
import * as types from './../types';
import * as constants from './../constants';
import { operations as toolbarOperations } from '../ducks/toolbar';

const {
  MOBILE_DEVICE_STATES,
  MOBILE_SESSION_STATES,
} = constants;

const initialState = Map({
  appVersion: null,
  // FIXME we may want to come up with a proper naming convention -
  // in other places we call the `deviceDescriptor` simply `device`
  // here `device` is the connected device the session client provides (gs)
  device: null,
  deviceDescriptor: null,
  deviceOrientation: null,
  deviceAnimationsEnabled: false,
  deviceToolsEnabled: false,
  deviceWifiEnabled: true,
  // santa clara data center
  gpsLocation: {
    latitude: 37.3894698,
    longitude: -121.9643038,
    zoom: 9,
  },
  isPerformanceModeEnabled: true,
  isWebRtcAudioEnabled: true,
  languages: {
    loading: true,
    items: {},
  },
  sessionPlatform: constants.FORM_TAB_STATES.PLATFORM_TYPE.WEB,
  sessionState: constants.MOBILE_SESSION_STATES.INITIAL,
  startedByTheUser: false,
  testReportId: null,
});

function getSessionState(deviceState, state) {
  switch (deviceState) {
    case MOBILE_DEVICE_STATES.OFFLINE:
    case MOBILE_DEVICE_STATES.CLOSING:
      return MOBILE_SESSION_STATES.DEVICE_DISCONNECTED;
    case MOBILE_DEVICE_STATES.LAUNCH:
    case MOBILE_DEVICE_STATES.VIDEO:
    case MOBILE_DEVICE_STATES.BOOTED:
    case MOBILE_DEVICE_STATES.INPUT:
    case MOBILE_DEVICE_STATES.UI:
      return MOBILE_SESSION_STATES.PREPARING;
    case MOBILE_DEVICE_STATES.ONLINE:
      if (state.get('startedByTheUser') && state.get('sessionState') !== MOBILE_SESSION_STATES.READY) {
        return MOBILE_SESSION_STATES.PREPARING;
      }

      return MOBILE_SESSION_STATES.READY;
    default:
      return MOBILE_SESSION_STATES.INITIAL;
  }
}

function setDeviceToolsEnabled(state) {
  return state.set('deviceToolsEnabled', !!(state.get('device') && state.get('deviceDescriptor')));
}

const mobileSessionReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case types.MOBILE_DEVICE_FETCH.SUCCESS:
      return setDeviceToolsEnabled(
        state.set('deviceDescriptor', new realDeviceSelectionRecords.DeviceDescriptor(payload)),
      );
    case types.MOBILE_DEVICE_FETCH.FAILURE:
      return setDeviceToolsEnabled(state.set('deviceDescriptor', initialState.get('deviceDescriptor')));
    case types.MOBILE_SESSION_CREATE.REQUEST:
      return state.withMutations((mutable) => {
        mutable.set('sessionState', MOBILE_SESSION_STATES.REQUESTING);
        mutable.set('startedByTheUser', true);
        mutable.set('testReportId', null);
      });
    case types.MOBILE_SESSION_CREATE.SUCCESS:
      return state.withMutations((mutable) => {
        mutable.set('sessionState', MOBILE_SESSION_STATES.ALLOCATING);
        mutable.set('testReportId', payload.testReportId);
      });
    case types.MOBILE_SESSION_CREATE.FAILURE: {
      return state.withMutations((mutable) => {
        mutable.set('sessionState', MOBILE_SESSION_STATES.SESSION_CREATION_FAILED);
        mutable.set('testReportId', null);
      });
    }
    case types.MOBILE_DEVICE_CONNECT.SUCCESS: {
      return setDeviceToolsEnabled(
        state.withMutations((mutable) => {
          mutable.set('device', payload);
          mutable.set('testReportId', payload.testReportId);
        }),
      );
    }
    case types.MOBILE_DEVICE_CONNECT.FAILURE:
      return setDeviceToolsEnabled(state.set('device', initialState.get('device')).set('sessionState', MOBILE_SESSION_STATES.DEVICE_CONNECTION_FAILED));
    case types.MOBILE_DEVICE_STATE_UPDATE:
      return state.set('sessionState', getSessionState(payload, state));
    case types.MOBILE_DEVICE_APP_INSTALL.REQUEST:
      return state.set('sessionState', MOBILE_SESSION_STATES.INSTALLING);
    case types.MOBILE_DEVICE_APP_INSTALL.SUCCESS:
    case types.MOBILE_DEVICE_OPEN_URL.SUCCESS:
    case types.MOBILE_DEVICE_ERROR_OVERLAY_DISMISS:
      return state.set('sessionState', MOBILE_SESSION_STATES.READY);
    case types.MOBILE_DEVICE_APP_INSTALL.FAILURE: {
      // TODO handle error message (gs)
      console.log('App installation failed', payload); // eslint-disable-line no-console
      return state.set('sessionState', MOBILE_SESSION_STATES.APP_INSTALLATION_FAILED);
    }
    case types.MOBILE_DEVICE_ANIMATIONS_TOGGLE.SUCCESS:
      return state.update('deviceAnimationsEnabled', (current) => !current);
    case types.MOBILE_DEVICE_ROTATE.REQUEST:
    case types.MOBILE_DEVICE_ROTATE.SUCCESS:
      return state.set('deviceOrientation', payload);
    case types.MOBILE_DEVICE_WIFI_TOGGLE.SUCCESS:
      return state.update('deviceWifiEnabled', (current) => !current);
    case types.MOBILE_SESSION_APP_INFO_FETCH.SUCCESS:
      return state.set('appVersion', new appStorageRecords.AppStorageFile(payload));
    case types.MOBILE_SESSION_APP_INFO_FETCH.FAILURE:
      return state.set('appVersion', initialState.get('appVersion'));
    case types.MOBILE_SESSION_STOP.SUCCESS:
      return initialState
        .set('testReportId', state.get('testReportId'));
    case types.MOBILE_DEVICE_LANGUAGES_FETCH.SUCCESS:
      return state.set('languages', {
        loading: false,
        items: mapKeys(payload, 'id'),
      });
    case types.MOBILE_DEVICES_PERFORMANCE_MODE_SET:
    case toolbarOperations.setPerformanceMode.fulfilled.type:
      return state.set('isPerformanceModeEnabled', payload);
    case types.TOGGLE_WEBRTC__AUDIO:
      return state.set('isWebRtcAudioEnabled', payload.isWebRtcAudioEnabled);
    default:
      return state;
  }
};

export default mobileSessionReducer;
export { initialState };
